import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { User } from '@shared/models';
import { cloneDeep } from 'lodash-es';
import { ClientWithAssignments, GridDrawerComponent } from '../../models';
import { ClientService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-upsert-client',
  templateUrl: 'upsert-client.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertClientComponent
  extends GridDrawerComponent<ClientWithAssignments>
  implements OnInit
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly clientService = inject(ClientService);

  public readonly maxNameLength = 255;
  enableUpdate: boolean = false;

  public form: FormGroup = this.formBuilder.group({
    name: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxNameLength),
        Validators.pattern(fieldsValidators.letterRequired),
      ],
    ],
    isActive: true,
  });

  get nameField(): AbstractControl<string> {
    return this.form.get('name')!;
  }

  private prevModel!: ClientWithAssignments;

  destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: ClientWithAssignments) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.prevModel!,
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });
    if (!this.model) return;

    this.clientService
      .getClientAssignment(this.model.clientId)
      .subscribe(clientAssignments => {
        this.initForm(clientAssignments);
      });
  }

  initForm(clientAssignments: User[]): void {
    this.form.patchValue({
      name: this.model?.name,
      isActive: !!this.model?.isActive,
    });

    this.prevModel = cloneDeep({
      ...this.model!,
      clientAssignments,
    });
  }

  private checkChanges(values: ClientWithAssignments): void {
    if (values && this.prevModel) {
      if (
        values.name !== this.prevModel.name ||
        values.isActive !== this.prevModel.isActive
      ) {
        this.enableUpdate = true;
      } else {
        this.enableUpdate = false;
      }
    }
  }
}
